/* eslint-disable sonarjs/no-duplicate-string */
import { Flex } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import * as React from 'react';

import { TreeNodeRenderFunction } from '@/components/shared/Tree/types';
import TreeFile from '@/components/shared/TreeFile';
import TreeFolder from '@/components/shared/TreeFolder';
import FieldCheckbox from '@/components/shared/form/FieldCheckbox';
import { getDocumentLockMessage } from '@/entities/Document/documentContentLock';
import { TreeNodeFile, TreeNodeFolder, getDocumentAvailabilityIconReason } from '@/entities/Tree';
import { DocumentsTreeSelectionStore } from '@/stores/local/shared/DocumentsTreeSelectionStore';
import { TreeStore } from '@/stores/local/shared/TreeStore';

import { DocumentsTreeViewParams } from './types';

const buildCheckbox = (checked: boolean, indeterminate: boolean, onToggle: () => void, disabled?: boolean) => (
  <Flex align="center" gap="xxs">
    <FieldCheckbox checked={checked} indeterminate={indeterminate} onToggle={onToggle} disabled={disabled} />
  </Flex>
);

const createSelectionActions = (
  selectionStore: DocumentsTreeSelectionStore,
  store: TreeStore,
  mode: 'multi-select' | 'select',
) => {
  const { checked, indeterminate } = selectionStore.getNodeState(store);
  const isFileLocked = store.data.type === 'file' && !(store.data as TreeNodeFile).contentAvailable;
  const handleToggle =
    mode === 'select' ? () => selectionStore.selectOnlyOrClear(store) : () => selectionStore.toggleNode(store);

  return buildCheckbox(checked, mode === 'multi-select' ? indeterminate : false, handleToggle, isFileLocked);
};

const resolveFolderActions = (
  params: DocumentsTreeViewParams,
  store: TreeStore,
  model: TreeNodeFolder,
): React.ReactNode => {
  switch (params.view) {
    case 'actions':
      return params.renderFolderActions?.(store, model);
    case 'multi-select':
      return createSelectionActions(params.selectionStore, store, 'multi-select');
    case 'select':
      return createSelectionActions(params.selectionStore, store, 'select');
    default:
      return null;
  }
};

const resolveFileHandlers = (params: DocumentsTreeViewParams, store: TreeStore, model: TreeNodeFile) => {
  switch (params.view) {
    case 'actions':
      return {
        onClick: () => params.onFileClick(store, model),
        actions: params.renderFileActions?.(store, model),
        indicator: params.renderFileIndicator?.(store, model),
      };
    case 'multi-select':
      return {
        onClick: () => {
          if (!model.contentAvailable) {
            notifications.show({
              color: 'yellow',
              title: 'Документ недоступен',
              message: getDocumentLockMessage(getDocumentAvailabilityIconReason(model)),
              autoClose: 6000,
            });

            return;
          }

          params.selectionStore.toggleNode(store);
        },
        actions: createSelectionActions(params.selectionStore, store, 'multi-select'),
        indicator: null,
      };
    case 'select':
      return {
        onClick: () => {
          if (!model.contentAvailable) {
            notifications.show({
              color: 'yellow',
              title: 'Документ недоступен',
              message: getDocumentLockMessage(getDocumentAvailabilityIconReason(model)),
              autoClose: 6000,
            });

            return;
          }

          params.selectionStore.selectOnlyOrClear(store);
        },
        actions: createSelectionActions(params.selectionStore, store, 'select'),
        indicator: null,
      };
    default:
      return {
        onClick: store.toggle,
        actions: null,
        indicator: null,
      };
  }
};

export const createDocumentsTreeRenderNode = (params: DocumentsTreeViewParams): TreeNodeRenderFunction => {
  return (store: TreeStore) => {
    const data = store.data;

    if (!data) {
      return null;
    }

    if (data.type === 'folder') {
      const folderClick = params.onFolderClick;

      const onClick = folderClick ? () => folderClick(store, data) : store.toggle;

      const actions = resolveFolderActions(params, store, data);

      // eslint-disable-next-line react/jsx-no-bind
      return <TreeFolder key={data.id} store={store} model={data} actions={actions} onClick={onClick} />;
    }

    if (data.type === 'file') {
      const { onClick, actions, indicator } = resolveFileHandlers(params, store, data);

      return (
        <TreeFile key={data.id} store={store} model={data} actions={actions} indicator={indicator} onClick={onClick} />
      );
    }

    return null;
  };
};
